import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Drawer, Popover, Space, Tooltip } from 'antd';
import { IoNotificationsOutline } from 'react-icons/io5';
import PropTypes from 'prop-types';
import { SettingFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { getPongRequestNotifications } from '../../actions/api-actions';
import { ApiService } from '../api/api-service';
import NotificationsCardView from '../../components/cp-notifications/cp-notifications';
import { notificationsConstant } from '../../constants';
import { getLoginAccountId, getLoginAccessToken, getLoginRes } from '../../services/auth-details-service';
import { basePath } from '../../utilities/utility';
import { getSession } from '../../services/storage-service';
import logger from '../../services/logger';

const NotificationsService = ({ iconColor }) => {
  const accountId = getLoginAccountId();
  const accessToken = getLoginAccessToken();
  const isLoggedIn = Object.keys(getLoginRes()).length > 0;
  const [open, setOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [ settingsOpen, setsettingsOpen] = useState(false);
  const baseUrl = basePath();
  const history = useHistory();
  // getting device type based on the screen resolution.
  const isMobileDevice = getSession('DEVICE_TYPE');
  let streamSessionId;
  let eventSource;

  const hideSettings = () =>{
    setsettingsOpen(false);
  }
  const handleOpenChange = (newOpen) => {
    setsettingsOpen(newOpen);
  };

  // This "showDrawer" function will show the drawer
  const showDrawer = () => {
    setOpen(!open);
  };

  // This "hideDrawer" function will hide the drawer
  const hideDrawer = () => {
    setOpen(false);
  };

  // This "handleNotificationTitleDescription" function will format the notification display text and title
  const handleNotificationTitleDescription = (displayText, entities) => {
    displayText = displayText?.replace(/\${creator\.name}/g, entities?.creator?.name);
    displayText = displayText?.replace(/\${bizRole\.name}/g, entities?.bizRole?.name);
    displayText = displayText?.replace(/\${team\.role}/g, entities?.team?.role);
    displayText = displayText?.replace(/\${team\.title}/g, entities?.team?.title);
    displayText = displayText?.replace(/\${perspective\.title}/g, entities?.perspective?.title);
    displayText = displayText?.replace(/\${service\.title}/g, entities?.service?.title);
    displayText = displayText?.replace(/\${transaction\.txnLabel}/g, entities?.transaction?.txnLabel);
    displayText = displayText?.replace(/\${transaction\.txnId}/g, entities?.transaction?.txnId);
    return displayText;
  };

  // This useEffect is used to handle the SSE INTEGRATION
  useEffect(() => {
    // Request permission for notifications
    Notification.requestPermission();

    if (isLoggedIn) {
      const streamUrl = ApiService.baseURL.replace("/bapi", "");
      // Create a new EventSource instance and specify the SSE endpoint
      eventSource = new EventSource(`${streamUrl}/stream?acctId=${accountId}&token=${accessToken}&device=WEB`);

      // Event listener when a connection to the server is opened
      eventSource.addEventListener('open', function (event) {
        logger.info('Stream is open');
        if (event && event.data) {
          streamSessionId = event.data;
        }
      });

      // Event listener for incoming SSE messages
      eventSource.addEventListener('event', function (event) {
        const data = JSON.parse(event.data);

        // Increment the notification count
        setNotificationCount((prevCount) => prevCount + 1);

        // Check if the browser supports notifications
        if ('Notification' in window) {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              const notification = new Notification(handleNotificationTitleDescription(data?.title, data?.entities), {
                body: handleNotificationTitleDescription(data?.display, data?.entities),
                icon: require('../../static/img/icon.png'),
              });
              notification.onclick = function () {
                logger.info('Notification clicked.');
                showDrawer();
                setNotificationCount(0);
              };
            }
          });
        }
      });

      // Event listener for incoming ping request coming from the server
      eventSource.addEventListener('ping', async function () {
        try {
          await getPongRequestNotifications(accountId, streamSessionId);
        } catch (error) {
          logger.error('Error:', error);
        }
      });

      // Event listener for SSE errors
      eventSource.onerror = (error) => {
        logger.error('SSE Error:', error);
        eventSource.close();
      };
    }

    // Cleanup the EventSource connection on component unmount or when isLoggedIn becomes false
    return () => {
      if (eventSource) {
        eventSource.close();
        logger.info('Stream is closed!');
      }
    };
  }, [isLoggedIn, accountId, accessToken]);

  useEffect(() => {
    if (notificationCount > 0 && open) {
      hideDrawer(); // Close the drawer if open and new notification count > 0
    }
  }, [notificationCount]);

  const handleNavigate = () => {
    hideSettings();
    hideDrawer();
    history.push({
      pathname: `${baseUrl}/account/profileinfo/${accountId}`,
    });
  };

  const handlePopoverContentNavigate = () => {
    return (
      <>
        <div style={{ display: 'flex' }}>
          <SettingFilled className="notifications-setting-icon" />
          <h5 className="notifications-setting-heading" onClick={handleNavigate}>
            Settings
          </h5>
        </div>
      </>
    );
  };

  return (
    <>
      <div className={`${isMobileDevice === 'MOBILE' ? 'mobile_header-bell-icon-div' : 'header-bell-icon-div'}`} >
        <Badge className="notification-count-badge" count={notificationCount}>
          <Tooltip overlayClassName="header-icons-tooltip" title="Notifications" placement="bottomLeft" overlayStyle={{ zIndex: 10000 }}>
            <IoNotificationsOutline
            className='notification-icon'
             onClick={() => {
                showDrawer();
                setNotificationCount(0);
              }} style={{ color: notificationCount > 0 ? '#f44336' : `${iconColor}` }} />
          </Tooltip>
        </Badge>
        {/* {notificationCount > 0 && <i className="notification-count">{notificationCount}</i>} */}
      </div>
      {open && 
        <Drawer
          className="notifications-drawer"
          title={notificationsConstant.TITLE}
          placement="right"
          width={420}
          height={378}
          onClose={hideDrawer}
          open={open}
          autoFocus={false}
          closeIcon={null}
          extra={
            <Space>
              <Popover
                overlayClassName="notifications-settings-popover"
                placement="bottomLeft"
                title=""
                content={handlePopoverContentNavigate()}
                trigger="click"
                open={settingsOpen}
                onOpenChange={handleOpenChange}
              >
                <FontAwesomeIcon className="notifications-more-icon" icon={faEllipsisVertical} />
              </Popover>
            </Space>
          }
        >
          <NotificationsCardView open={open} hideDrawer={hideDrawer} />
        </Drawer>
      }

    </>
  );
};

NotificationsService.propTypes = {
  iconColor: PropTypes.string,
};

export default NotificationsService;
